import UpgradeTooltip from '@anm/components/UpgradeTooltip';
import Dialog from '@anm/components/modals/Dialog';
import ConfirmDelete from '@anm/components/notification/ConfirmDelete';
import useDependPendingCallback from '@anm/hooks/useDependPendingCallback';
import { useState, FC } from 'react';
import { UserTeamPermission } from 'types/user';

import InvitationItem, { InvitationItemInputProps } from './InvitationItem';
import TeamRolesModal, { TeamRolesModalProps } from './TeamRolesModal';
import Wrapper, { Grid, Header, Limit, Title } from './Wrapper';

export type InvitationsListProps = Pick<TeamRolesModalProps, 'onRoleChange'> & {
  limit: number;
  isChangeRolePending: boolean;
  isDeletePending: boolean;
  canUpgrade: boolean;
  list: InvitationItemInputProps[];
  onMemberDelete: (id: string) => void;
  onInviteDelete: (hash: string) => void;
  onInviteResend: (id: string) => void;
  permissions?: UserTeamPermission;
};

const InvitationsList: FC<InvitationsListProps> = ({
  list,
  limit,
  canUpgrade,
  isChangeRolePending,
  isDeletePending,
  permissions,
  onRoleChange,
  onMemberDelete,
  onInviteResend,
  onInviteDelete
}) => {
  const [userDeleteId, setUserDeleteId] = useState<string | null>(null);
  const [inviteDeleteId, setInviteDeleteId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const teamMembers = list.filter(({ isTeamMember }) => isTeamMember);

  useDependPendingCallback(isChangeRolePending, () => setUserId(null));
  useDependPendingCallback(isDeletePending, () => {
    setUserDeleteId(null);
    setInviteDeleteId(null);
  });

  return (
    <Wrapper>
      {userDeleteId && (
        <Dialog onModalClose={() => setUserDeleteId(null)} onSubmit={() => onMemberDelete(userDeleteId)}>
          <ConfirmDelete
            title="Delete team member"
            description={`The Team member will be permanently<br/> removed from your team account, so<br/> they cannot see or create projects<br/> in the Team workspace.`}
            isPending={isDeletePending}
            onClose={() => setUserDeleteId(null)}
            onDelete={() => onMemberDelete(userDeleteId)}
          />
        </Dialog>
      )}
      {inviteDeleteId && (
        <Dialog onModalClose={() => setInviteDeleteId(null)} onSubmit={() => onInviteDelete(inviteDeleteId)}>
          <ConfirmDelete
            title="Delete invite"
            description={`The Team invite will be permanently removed.<br/> You can create new one`}
            isPending={isDeletePending}
            onClose={() => setInviteDeleteId(null)}
            onDelete={() => onInviteDelete(inviteDeleteId)}
          />
        </Dialog>
      )}
      {userId && (
        <TeamRolesModal
          userId={userId}
          permissions={permissions}
          isPending={isChangeRolePending}
          list={teamMembers}
          onRoleChange={onRoleChange}
          onModalClose={() => setUserId(null)}
        />
      )}
      <Header>
        <Title>Property Experts Ltd Team</Title>
        <Limit isLink={canUpgrade}>
          <UpgradeTooltip
            minWidth={150}
            canShow={canUpgrade}
            buttonLink="/pricing"
            buttonText="Upgrade"
            text="Please upgrade to add more members."
          >
            {teamMembers.length}/{limit} seats
          </UpgradeTooltip>
        </Limit>
      </Header>
      <Grid>
        {list.map(props => (
          <InvitationItem
            key={`${props?.inviteKey}/${props?.userId}`}
            {...props}
            permissions={permissions}
            onChange={setUserId}
            onUserDelete={setUserDeleteId}
            onInviteDelete={setInviteDeleteId}
            onInviteResend={onInviteResend}
          />
        ))}
      </Grid>
    </Wrapper>
  );
};

export default InvitationsList;
